import { type Block, type LayoutFn, type Line } from "./base";
import { markTableLine } from "./table-tag";
import { hl, pad, wrapText } from "./text";
import { normalizedWidth } from "./base";

function tableSep(
  ws: number[],
  l: string,
  m: string,
  r: string,
  color?: string,
): Line {
  const line: Line = [{ text: l, color, noSelect: true }];
  for (let i = 0; i < ws.length; i++) {
    line.push({ text: hl(ws[i]!), color, noSelect: true, cell: i });
    line.push({ text: i < ws.length - 1 ? m : r, color, noSelect: true });
  }
  return line;
}

function tableTop(ws: number[], color?: string): Line {
  return tableSep(ws, "\u250c", "\u252c", "\u2510", color);
}

function tableMid(ws: number[], color?: string): Line {
  return tableSep(ws, "\u251c", "\u253c", "\u2524", color);
}

function tableBot(ws: number[], color?: string): Line {
  return tableSep(ws, "\u2514", "\u2534", "\u2518", color);
}

export type Column<T extends Record<string, unknown> = Record<string, unknown>> = {
  key: keyof T & string;
  title?: string;
  width?: number;
  align?: "left" | "right";
  headerColor?: string;
  headerBold?: boolean;
  color?: string | ((value: unknown, row: T) => string | undefined);
  dim?: boolean | ((value: unknown, row: T) => boolean);
  onClick?: (value: unknown, row: T) => void;
};

export function table<T extends Record<string, unknown>>(
  columns: Column<T>[],
  data: T[],
  opts?: { borderColor?: string; fillWidth?: boolean; headerColor?: string; cellPadding?: number },
): LayoutFn {
  return (w) => {
    const bc = opts?.borderColor;
    const hc = opts?.headerColor;
    const cp = normalizedWidth(opts?.cellPadding ?? 1);
    const nSep = columns.length + 1; // separators: │col│col│...│
    const minColWidth = 1;
    const widthBudget = Math.max(0, normalizedWidth(w) - nSep);
    const padStr = " ".repeat(cp);

    // Compute column widths
    const ws = columns.map((col) => {
      if (col.width) return Math.max(minColWidth, col.width);
      const displayTitle = col.title ?? col.key;
      const titleW = displayTitle.length + 2 * cp;
      const dataW = Math.max(
        ...data.map((row) => String(row[col.key] ?? "").length + 2 * cp),
        0,
      );
      return Math.max(minColWidth, titleW, dataW);
    });

    // If fillWidth, distribute remaining space
    if (opts?.fillWidth && widthBudget > 0) {
      const used = ws.reduce((a, b) => a + b, 0);
      const remaining = widthBudget - used;
      if (remaining > 0 && ws.length > 0) {
        const even = Math.floor(remaining / ws.length);
        const rem = remaining % ws.length;
        for (let i = 0; i < ws.length; i++) {
          ws[i] = ws[i]! + even + (i < rem ? 1 : 0);
        }
      }
    }

    if (widthBudget > 0) {
      let used = ws.reduce((a, b) => a + b, 0);
      let overflow = used - widthBudget;
      while (overflow > 0) {
        let idx = -1;
        let widest = 0;
        for (let i = 0; i < ws.length; i++) {
          if (ws[i]! > minColWidth && ws[i]! > widest) {
            widest = ws[i]!;
            idx = i;
          }
        }
        if (idx === -1) break;
        ws[idx] = ws[idx]! - 1;
        overflow -= 1;
      }
    }

    const result: Block = [];

    // Top border
    result.push(tableTop(ws, bc));

    // Header row
    const headerLine: Line = [{ text: "\u2502", color: bc, noSelect: true }];
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i]!;
      const displayTitle = col.title ?? col.key;
      const innerW = Math.max(0, ws[i]! - 2 * cp);
      const text = padStr + pad(displayTitle, innerW, col.align === "right") + padStr;
      headerLine.push({
        text,
        color: col.headerColor ?? hc,
        bold: col.headerBold ?? true,
        cell: i,
      });
      headerLine.push({ text: "\u2502", color: bc, noSelect: true });
    }
    result.push(headerLine);

    // Mid separator
    result.push(tableMid(ws, bc));

    // Data rows — wrap cell text instead of truncating
    for (const row of data) {
      const wrapped: string[][] = columns.map((col, ci) => {
        const val = String(row[col.key] ?? "");
        const innerW = Math.max(1, ws[ci]! - 2 * cp);
        return wrapText(val, innerW);
      });
      const rowHeight = Math.max(...wrapped.map((c) => c.length), 1);

      for (let ln = 0; ln < rowHeight; ln++) {
        const dataLine: Line = [{ text: "\u2502", color: bc, noSelect: true }];
        for (let i = 0; i < columns.length; i++) {
          const col = columns[i]!;
          const cellLine = wrapped[i]![ln] ?? "";
          const innerW = Math.max(0, ws[i]! - 2 * cp);
          const text = padStr + pad(cellLine, innerW, col.align === "right") + padStr;
          const color =
            typeof col.color === "function"
              ? col.color(row[col.key], row)
              : col.color;
          const dim =
            typeof col.dim === "function"
              ? col.dim(row[col.key], row)
              : col.dim;
          const cellClick = typeof col.onClick === "function" ? () => col.onClick!(row[col.key], row) : undefined;
          dataLine.push({ text, color: color || undefined, dim: dim || undefined, onClick: cellClick, cell: i });
          dataLine.push({ text: "\u2502", color: bc, noSelect: true });
        }
        result.push(dataLine);
      }
    }

    // Bottom border
    result.push(tableBot(ws, bc));

    // Tag all lines so the renderer can emit HTML <table>
    for (const line of result) markTableLine(line);
    return result;
  };
}
